import type { DatoQueSeQueda, EstadoSugerencia, MotivoSugerencia } from '../../../api/ops/clientesDuplicados';
import type { NombreIcono } from '../../../componentes/iconos';
import type { TarjetaAyuda } from '../../../marco/cabecera';

/**
 * Textos de «Duplicados» (2.5), tal cual del diseño (`2.5-duplicados.html`) salvo donde una decisión lo corrige: G200
 * («Confirmo que son la misma persona» cuando la API pide confirmación), G160 (fusionar no se deshace desde el panel),
 * G159 («Son personas distintas» con «Deshacer» 5 s), G140 (filtros sin cifras) y G192 (sin «Alergias» en los ejemplos).
 */
export const PERMISOS_DUPLICADOS = { ver: 'clientes.duplicados.ver', fusionar: 'clientes.duplicados.fusionar' } as const;

export const FILTROS: readonly { estado: EstadoSugerencia; etiqueta: string }[] = [
    { estado: 'open', etiqueta: 'Por revisar' },
    { estado: 'merged', etiqueta: 'Fusionadas' },
    { estado: 'dismissed', etiqueta: 'Descartadas' },
];

export const MOTIVO: Record<MotivoSugerencia, [NombreIcono, string]> = {
    local_matches_linked: ['phone', 'Mismo teléfono que un cliente con Cuenta Reservaria'],
    same_email: ['mail', 'Mismo email con distinto teléfono'],
    // G359: el teléfono o el Instagram tecleado en una ficha ya es de otro cliente (encargo de la exportación, 2.5 punto 16).
    same_phone: ['phone', 'Mismo teléfono'],
    same_instagram: ['instagram', 'Mismo Instagram'],
    similar_name: ['user', 'Nombre parecido y mismo teléfono local'],
    import_doubtful: ['file-spreadsheet', 'Dudoso en una importación'],
    last_digits_v1: ['history', 'Mismos 9 últimos dígitos (de la v1)'],
};

export const ORIGEN: Record<string, string> = {
    panel: 'Alta en el panel',
    tablet: 'Tablet (sin reserva)',
    web: 'Web',
    widget: 'Widget',
    chat: 'Chat',
    assistant: 'Asistente',
    list_link: 'Lista de invitados',
    import: 'Importación',
    migration_v1: 'Migración de la v1',
    api: 'API',
};

export const IDIOMA: Record<string, string> = { 'es-ES': 'Español', 'en-GB': 'Inglés', 'fr-FR': 'Francés', 'de-DE': 'Alemán' };

export const CANALES: readonly [string, string][] = [
    ['whatsapp', 'WhatsApp'],
    ['email', 'Email'],
    ['instagram', 'Instagram'],
    ['telegram', 'Telegram'],
    ['sms', 'SMS'],
];
export const FINALIDADES: readonly [string, string][] = [
    ['service', 'servicio'],
    ['marketing', 'comercial'],
];
export const ESTADO_PERMISO: Record<string, [string, 'confirmed' | 'problem' | 'pending' | 'neutral']> = {
    granted: ['Concedido', 'confirmed'],
    withdrawn: ['Baja', 'problem'],
    pending_confirmation: ['Pendiente', 'pending'],
    none: ['Sin permiso', 'neutral'],
};

export const DATOS_QUE_SE_QUEDAN: readonly [DatoQueSeQueda, string][] = [
    ['local_display_name', 'Nombre en tu negocio'],
    ['local_phone', 'Teléfono aportado'],
    ['local_email', 'Email aportado'],
    ['locale', 'Idioma'],
    ['field_values', 'Campos propios'],
];

export const VACIA: Record<EstadoSugerencia, { titulo: string; texto: string }> = {
    open: { titulo: 'No hay duplicados por revisar.', texto: 'Reservaria busca cada noche fichas que pueden ser la misma persona. Cuando encuentre alguna, la verás aquí.' },
    merged: { titulo: 'Todavía no has fusionado ninguna ficha.', texto: 'Las fusiones que hagas quedarán aquí, con su fecha.' },
    dismissed: { titulo: 'No has descartado ninguna sugerencia.', texto: 'Cuando marques una pareja como personas distintas, la verás aquí y no se volverá a sugerir.' },
};

export const AYUDA: Record<'lista' | 'comparar' | 'historial' | 'elegir' | 'fusionada', TarjetaAyuda[]> = {
    lista: [
        { icono: 'git-compare', titulo: 'Duplicados', texto: 'Cada noche, y al dar de alta o importar, Reservaria busca fichas que pueden ser la misma persona en tu negocio. Nunca las une sola.' },
        { icono: 'git-merge', titulo: 'Fusionar', texto: 'Eliges el perfil principal y qué datos se quedan. Todo lo demás se suma: reservas, pagos, notas, etiquetas e historial.' },
        { icono: 'shield-check', titulo: 'Gana la baja', texto: 'Si una de las dos fichas se dio de baja de un canal, la ficha fusionada queda con la baja.' },
    ],
    comparar: [
        { icono: 'user-cog', titulo: 'Principal', texto: 'Es la ficha que se queda. La otra se une a ella y sus enlaces llevan a la principal.' },
        { icono: 'lock', titulo: 'Cuenta Reservaria', texto: 'El nombre, el teléfono y el email verificados son del cliente: fusionar no los cambia.' },
        { icono: 'shield-check', titulo: 'Gana la baja', texto: 'Los permisos se unen con todo su historial. Si uno tenía la baja de un canal, la fusión también.' },
    ],
    historial: [{ icono: 'history', titulo: 'Historial', texto: 'Lo que ha hecho cada ficha en tu negocio. Al fusionar se juntan sin perder ninguna línea.' }],
    elegir: [{ icono: 'search', titulo: 'Fusionar a mano', texto: 'Busca las dos fichas por nombre, teléfono o email. Solo verás clientes de tu negocio.' }],
    fusionada: [{ icono: 'git-merge', titulo: 'Fusionada', texto: 'Las dos fichas son ya una sola. Sus reservas, pagos y conversaciones están en la principal.' }],
};

export const TEXTOS = {
    soloVerRol: 'Con tu rol ves las sugerencias. Fusionar o descartar lo hace el propietario o un administrador.',
    soloVerNegocio: 'El negocio está en solo lectura: puedes revisar las sugerencias, pero no fusionar ni descartar.',
    compararRol: 'Con tu rol puedes comparar las fichas. Fusionar lo hace el propietario o un administrador.',
    compararNegocio: 'El negocio está en solo lectura: puedes comparar, pero no fusionar.',
    descartada: 'Marcaste esta pareja como personas distintas: no se vuelve a sugerir. Si te equivocaste, puedes unirlas con «Fusionar dos clientes».',
    noSeVuelven: 'No se volverán a sugerir.',
    sonDistintasNota: 'La pareja no se vuelve a sugerir. Tienes 5 segundos para deshacerlo.',
    noSeDeshace: 'Fusionar no se puede deshacer desde el panel. El otro perfil queda unido al principal y sus enlaces llevan a él.',
    fusionadaNota: 'No se puede deshacer desde el panel. Si fue un error, escribe a Soporte de Reservaria: guarda cómo estaban los dos perfiles durante 30 días.',
    permisosAyuda: 'Se unen con todo su historial. Si uno de los dos se dio de baja, gana la baja.',
    seSumanAyuda: 'Nada se pierde: pasa todo al principal.',
    sinActividad: 'Todavía no tiene actividad.',
    /** G200: la API pide confirmación expresa cuando no comparten teléfono ni email. */
    confirmoMisma: 'Confirmo que son la misma persona',
    confirmoAviso: 'Estas dos fichas no comparten teléfono ni email. Fusiónalas solo si sabes que son la misma persona.',
} as const;

export const primerNombre = (nombre: string) => nombre.trim().split(/\s+/)[0] ?? nombre;
